import React, { useEffect } from 'react';
import { Button, Col, Form, Input, notification, Row, Select, Space, Typography, Upload, Radio } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CloseOutlined, ContainerOutlined, UploadOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getConfigInfo, updateConfig } from '../../services/config';
import { getPosts } from '../../services/post';
import { getAllCategories } from '../../services/category';
import BundledEditor from '../../components/BundledEditor';
import { uploadImageCDN, checkValidUpload } from '../../utils/utility';

const itemName = 'Cấu hình';
const { Text, Title } = Typography;

const TYPE_OPTIONS = [
  { value: 1, label: 'Chuỗi' },
  { value: 2, label: 'JSON' },
  { value: 3, label: 'Ảnh' },
  { value: 4, label: 'HTML' },
  { value: 5, label: 'Slide' },
  { value: 6, label: 'Banner' },
  { value: 7, label: 'Thư mục ảnh' },
];

const STATUS_OPTIONS = [
  { value: 1, label: 'Hoạt động' },
  { value: 0, label: 'Không hoạt động' },
];

const EditConfig = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const { data: itemInfo } = useQuery({ queryKey: ['configInfo', id], queryFn: () => getConfigInfo(id) });

  useEffect(() => {
    if (itemInfo) {
      form.setFieldsValue({
        key: itemInfo?.key,
        type: itemInfo?.type ?? 1,
        title: itemInfo?.title,
        value: itemInfo?.value,
        note: itemInfo?.note,
        status: itemInfo?.status,
      });
      // if type is slide, parse value JSON to slides array
      if (Number(itemInfo?.type) === 5) {
        try {
          const arr = typeof itemInfo?.value === 'string' ? JSON.parse(itemInfo.value) : itemInfo.value;
          if (Array.isArray(arr)) {
            // ensure order fields exist
            const slides = arr.map((s, i) => ({ title: s?.title || '', image: s?.image || '', link: s?.link || '', order: s?.order ?? i + 1 }));
            form.setFieldsValue({ slides });
          } else {
            form.setFieldsValue({ slides: [] });
          }
        } catch (e) {
          form.setFieldsValue({ slides: [] });
        }
      }
      // if type is banner, parse value JSON to banner object (no title field)
      if (Number(itemInfo?.type) === 6) {
        try {
          const obj = typeof itemInfo?.value === 'string' ? JSON.parse(itemInfo.value) : itemInfo.value;
          const banner = {
            image: obj?.image || '',
            link: obj?.link || '',
          };
          form.setFieldsValue({ banner });
        } catch (e) {
          form.setFieldsValue({ banner: { image: '', link: '' } });
        }
      }
      // if type is album (7), parse value JSON to album array
      if (Number(itemInfo?.type) === 7) {
        try {
          const arr = typeof itemInfo?.value === 'string' ? JSON.parse(itemInfo.value) : itemInfo.value;
          const album = Array.isArray(arr) ? arr.filter((u) => typeof u === 'string') : [];
          form.setFieldsValue({ album });
        } catch (e) {
          form.setFieldsValue({ album: [] });
        }
      }
      // if key is news-box, force type to JSON (2) and parse value into newsItems array
      if (itemInfo?.key === 'news-box') {
        try {
          const arr = typeof itemInfo?.value === 'string' ? JSON.parse(itemInfo.value) : itemInfo.value;
          const newsItems = Array.isArray(arr)
            ? arr.map((it, i) => ({
                // Now 'title' stores selected child category_id under parent 23
                title: Number(it?.title) > 0 ? Number(it.title) : undefined,
                icon: it?.icon || '',
                color: it?.color || 'black',
                order: Number(it?.order ?? i + 1),
                post_id: Number(it?.post_id) > 0 ? Number(it.post_id) : undefined,
              }))
            : [];
          form.setFieldsValue({ type: 2, newsItems });
        } catch (e) {
          form.setFieldsValue({ type: 2, newsItems: [] });
        }
      }
      // if key is web-params, force type to JSON (2) and parse value into webParams object
      if (itemInfo?.key === 'web-params') {
        try {
          const obj = typeof itemInfo?.value === 'string' ? JSON.parse(itemInfo.value) : (itemInfo?.value || {});
          const webParams = {
            base_color: obj?.['base-color'] || 'black',
            web_title: obj?.['web-title'] || '',
            favicon: obj?.favicon || '',
            logo: obj?.logo || '',
            share_image: obj?.['share-image'] || '',
            clock_icon: obj?.['clock-icon'] || '',
            gif_icon: obj?.['gif-icon'] || '',
            phone: obj?.phone || '',
            facebook_link: obj?.facebook_link || '',
            home_message: obj?.['home-message'] || '',
            home_description: obj?.['home-description'] || '',
            articles: Number(obj?.articles) === 1 ? 1 : 0,
            nav_bg_image: obj?.['nav-bg-image'] || '',
            footer_bg_image: obj?.['footer-bg-image'] || '',
            home_video_url: obj?.['home-video-url'] || '',
            // Accept object or string, but store as a single image URL string
            detail_top_banner: typeof obj?.['detail-top-banner'] === 'string'
              ? obj['detail-top-banner']
              : (obj?.['detail-top-banner']?.image || ''),
            trial_banner: typeof obj?.['trial-banner'] === 'string'
              ? obj['trial-banner']
              : (obj?.['trial-banner']?.image || ''),
            center_home_banner: typeof obj?.['center-home-banner'] === 'string'
              ? obj['center-home-banner']
              : (obj?.['center-home-banner']?.image || ''),
            detail_bottom_banner: Array.isArray(obj?.['detail-bottom-banner'])
              ? obj['detail-bottom-banner'].map((b) => ({ image: b?.image || '', url: b?.url || '' }))
              : [],
          };
          form.setFieldsValue({ type: 2, webParams });
        } catch (e) {
          form.setFieldsValue({ type: 2, webParams: { base_color: 'black', web_title: '', favicon: '', logo: '', share_image: '', clock_icon: '', gif_icon: '', phone: '', facebook_link: '', home_message: '', home_description: '', articles: 0, nav_bg_image: '', footer_bg_image: '', home_video_url: '', detail_top_banner: '', trial_banner: '', center_home_banner: '', detail_bottom_banner: [] } });
        }
      }
      // if key is footer-config, force type to JSON (2) and parse value into footerConfig object
      if (itemInfo?.key === 'footer-config') {
        try {
          const obj = typeof itemInfo?.value === 'string' ? JSON.parse(itemInfo.value) : (itemInfo?.value || {});
          const footerConfig = {
            gioi_thieu: obj?.['gioi-thieu'] || '',
            ho_tro: Array.isArray(obj?.['ho-tro']) ? obj['ho-tro'].filter((n) => Number(n) > 0).map((n) => Number(n)) : [],
            huong_dan: Array.isArray(obj?.['huong-dan']) ? obj['huong-dan'].filter((n) => Number(n) > 0).map((n) => Number(n)) : [],
          };
          form.setFieldsValue({ type: 2, footerConfig });
        } catch (e) {
          form.setFieldsValue({ type: 2, footerConfig: { gioi_thieu: '', ho_tro: [], huong_dan: [] } });
        }
      }
      // if key is class-config, force type to JSON (2) and parse value into classConfig object
      if (itemInfo?.key === 'class-config') {
        try {
          const obj = typeof itemInfo?.value === 'string' ? JSON.parse(itemInfo.value) : (itemInfo?.value || {});
          const items = Array.isArray(obj?.items)
            ? obj.items.map((it, i) => ({
                title: it?.title || '',
                type: Number(it?.type) === 2 ? 2 : 1,
                link: it?.link || '',
                image: it?.image || '',
                description: it?.description || '',
                bg_color: it?.['bg-color'] || '',
                order: i + 1,
              }))
            : [];
          const classConfig = {
            text_color: obj?.['text-color'] || '#ff922e',
            items,
          };
          form.setFieldsValue({ type: 2, classConfig });
        } catch (e) {
          form.setFieldsValue({ type: 2, classConfig: { text_color: '#ff922e', items: [] } });
        }
      }
    }
  }, [form, itemInfo]);

  const type = Form.useWatch('type', form);
  const keyValue = Form.useWatch('key', form);
  const isNewsBox = (itemInfo?.key === 'news-box') || (keyValue === 'news-box');
  const isWebParams = (itemInfo?.key === 'web-params') || (keyValue === 'web-params');
  const isFooterConfig = (itemInfo?.key === 'footer-config') || (keyValue === 'footer-config');
  const isClassConfig = (itemInfo?.key === 'class-config') || (keyValue === 'class-config');

  // Fetch posts for dropdowns by key
  // - news-box: category 23
  // - footer-config: category 1
  const { data: postsCat23 } = useQuery({
    queryKey: ['postsCat23'],
    queryFn: () => getPosts({ category_id: 23, status: [1], limit: 1000, page: 1 }),
    enabled: !!isNewsBox,
    staleTime: 1000 * 60 * 5,
  });
  const { data: postsCat1 } = useQuery({
    queryKey: ['postsCat1'],
    queryFn: () => getPosts({ category_id: 1, status: [1], limit: 1000, page: 1 }),
    enabled: !!isFooterConfig,
    staleTime: 1000 * 60 * 5,
  });
  const newsPostOptions = (postsCat23?.data || []).map((p) => ({ value: p.id, label: p.title }));
  const footerPostOptions = (postsCat1?.data || []).map((p) => ({ value: p.id, label: p.title }));

  // Fetch categories for news-box: we need child categories of parent id = 23
  const { data: allCategoriesForNewsBox } = useQuery({
    queryKey: ['allCategoriesForNewsBox'],
    queryFn: getAllCategories,
    enabled: !!isNewsBox,
    staleTime: 1000 * 60 * 10,
  });
  const newsCategoryOptions = (Array.isArray(allCategoriesForNewsBox?.data) ? allCategoriesForNewsBox.data : [])
    .filter((c) => Number(c?.parent_id) === 23)
    .map((c) => ({ value: c.id, label: c.title }))
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));

  // Fetch posts grouped by each child category under parent 23 for news-box
  const { data: newsPostsByCat } = useQuery({
    queryKey: ['newsPostsByChildCats', (allCategoriesForNewsBox?.data || []).length],
    enabled: !!isNewsBox && Array.isArray(allCategoriesForNewsBox?.data),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const allCats = Array.isArray(allCategoriesForNewsBox?.data) ? allCategoriesForNewsBox.data : [];
      const children = allCats.filter((c) => Number(c?.parent_id) === 23);
      if (children.length === 0) return {};
      const results = await Promise.all(children.map((c) => getPosts({ category_id: c.id, status: [1], limit: 1000, page: 1 })));
      const map = {};
      children.forEach((c, idx) => {
        const list = Array.isArray(results[idx]?.data) ? results[idx].data : [];
        map[c.id] = list;
      });
      return map; // { [catId]: posts[] }
    },
  });

  const { mutate: mutateUpdate, isPending } = useMutation({
    mutationFn: ({ id, payload }) => updateConfig(id, payload),
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Cập nhật ${itemName} thành công!` });
        navigate(-1);
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => {
      notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' });
    },
  });

  const handleUploadImage = async (file) => {
    const isValid = checkValidUpload(file);
    if (!isValid) return false;
    const url = await uploadImageCDN(file, () => {}, 'toancohien_config');
    if (url) {
      notification.success({ message: 'Tải lên thành công!' });
      const safeUrl = encodeURI(url);
      form.setFieldsValue({ value: safeUrl });
    }
    return false;
  };

  const handleSubmitForm = (data) => {
    const payload = {
      key: data?.key,
      type: Number(data?.type || 1),
      title: data?.title,
      value: data?.value,
      note: data?.note,
      status: data?.status,
    };
    // Special handling for web-params: build JSON structure and force type JSON (2)
    if (isWebParams) {
      // Read directly from form to ensure latest nested values (e.g., image uploads) are captured
      const wp = form.getFieldValue('webParams') || data?.webParams || {};
      const valueObj = {
        'base-color': wp?.base_color || 'black',
        'web-title': wp?.web_title || '',
        'favicon': wp?.favicon || '',
        'logo': wp?.logo || '',
        'share-image': wp?.share_image || '',
        'clock-icon': wp?.clock_icon || '',
        'gif-icon': wp?.gif_icon || '',
        'phone': wp?.phone || '',
        'facebook_link': wp?.facebook_link || '',
        'home-message': wp?.home_message || '',
        'home-description': wp?.home_description || '',
        'articles': Number(wp?.articles) === 1 ? 1 : 0,
        'nav-bg-image': wp?.nav_bg_image || '',
        'footer-bg-image': wp?.footer_bg_image || '',
        'home-video-url': wp?.home_video_url || '',
        // Serialize as a single image string
        'detail-top-banner': wp?.detail_top_banner || '',
        'trial-banner': wp?.trial_banner || '',
        'center-home-banner': wp?.center_home_banner || '',
        'detail-bottom-banner': Array.isArray(wp?.detail_bottom_banner)
          ? wp.detail_bottom_banner.map((b) => ({ image: b?.image || '', url: b?.url || '' }))
          : [],
      };
      payload.type = 2;
      payload.value = JSON.stringify(valueObj);
      mutateUpdate({ id, payload });
      return;
    }
    // Special handling for class-config
    if (isClassConfig) {
      const cc = form.getFieldValue('classConfig') || data?.classConfig || {};
      const items = Array.isArray(cc?.items) ? cc.items : [];
      const normalized = items
        .map((it, idx) => ({
          title: it?.title || '',
          type: Number(it?.type) === 2 ? 2 : 1,
          link: it?.link || '',
          image: it?.image || '',
          description: it?.description || '',
          ['bg-color']: it?.bg_color || '',
          order: Number(it?.order || idx + 1),
        }))
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
        .map((it, i) => ({ ...it, order: i + 1 }));
      const valueObj = {
        'text-color': cc?.text_color || '#ff922e',
        items: normalized.map(({ ['bg-color']: bgColor, ...rest }) => ({ ...rest, ['bg-color']: bgColor })),
      };
      payload.type = 2;
      payload.value = JSON.stringify(valueObj);
      mutateUpdate({ id, payload });
      return;
    }
    // Special handling for footer-config
    if (isFooterConfig) {
      const fc = form.getFieldValue('footerConfig') || data?.footerConfig || {};
      const valueObj = {
        'gioi-thieu': fc?.gioi_thieu || '',
        'ho-tro': Array.isArray(fc?.ho_tro) ? fc.ho_tro.filter((n) => Number(n) > 0).map((n) => Number(n)) : [],
        'huong-dan': Array.isArray(fc?.huong_dan) ? fc.huong_dan.filter((n) => Number(n) > 0).map((n) => Number(n)) : [],
      };
      payload.type = 2;
      payload.value = JSON.stringify(valueObj);
      mutateUpdate({ id, payload });
      return;
    }
    // Special handling for news-box: value is an array of objects, type always JSON (2)
    if (isNewsBox) {
      const items = Array.isArray(data?.newsItems) ? data.newsItems : [];
      // normalize and validate
      const normalized = [...items]
        .map((it, idx) => ({
          // store selected child category id as title field
          title: Number(it?.title) > 0 ? Number(it.title) : undefined,
          icon: it?.icon || '',
          color: it?.color || 'black',
          order: Number(it?.order || idx + 1),
          post_id: Number(it?.post_id) > 0 ? Number(it.post_id) : undefined,
        }))
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
        .map((it, i) => ({ ...it, order: i + 1 }));
      payload.type = 2;
      payload.value = JSON.stringify(normalized);
      mutateUpdate({ id, payload });
      return;
    }
    // If type is slide, convert slides array to JSON string
    if (Number(payload.type) === 5) {
      const slides = Array.isArray(data?.slides) ? data.slides : [];
      const invalid = slides.some((s) => !s?.image);
      if (invalid) {
        notification.error({ message: 'Mỗi slide cần có ảnh (image)!' });
        return;
      }
      // sort by order asc then normalize order
      const sorted = [...slides].sort((a, b) => (Number(a?.order || 0) - Number(b?.order || 0)) || 0)
        .map((s, i) => ({
          title: s?.title || '',
          image: s?.image || '',
          link: s?.link || '',
          order: i + 1,
        }));
      payload.value = JSON.stringify(sorted);
    }
    // If type is banner, convert banner object to JSON string (no title)
    if (Number(payload.type) === 6) {
      const bn = data?.banner || {};
      const banner = {
        image: bn?.image || '',
        link: bn?.link || '',
      };
      if (!banner.image) {
        notification.error({ message: 'Vui lòng chọn ảnh cho banner!' });
        return;
      }
      payload.value = JSON.stringify(banner);
    }
    // If type is album (7), serialize array of image links
    if (Number(payload.type) === 7) {
      const album = Array.isArray(data?.album) ? data.album.filter((u) => typeof u === 'string' && u.trim()) : [];
      payload.value = JSON.stringify(album);
    }
    // Optional: validate JSON if type=2
    if (Number(payload.type) === 2 && payload.value) {
      try {
        JSON.parse(payload.value);
      } catch (e) {
        notification.error({ message: 'Giá trị không phải JSON hợp lệ!' });
        return;
      }
    }
    mutateUpdate({ id, payload });
  };

  const renderValueField = () => {
    // Custom value editor for class-config
    if (isClassConfig) {
      const handleUploadClassItemImageAt = async (file, index) => {
        const isValid = checkValidUpload(file);
        if (!isValid) return false;
        const url = await uploadImageCDN(file, () => {}, 'toancohien_class_config');
        if (url) {
          const safeUrl = encodeURI(url);
          const current = Array.isArray(form.getFieldValue(['classConfig', 'items'])) ? [...form.getFieldValue(['classConfig', 'items'])] : [];
          current[index] = { ...(current[index] || {}), image: safeUrl };
          form.setFieldsValue({ classConfig: { ...(form.getFieldValue('classConfig') || {}), items: current } });
          notification.success({ message: 'Tải lên thành công!' });
        }
        return false;
      };

      const COLOR_OPTIONS = [
        { value: '#ff922e', label: 'Mặc định', hex: '#ff922e' },
        { value: '#ef4444', label: 'Red', hex: '#ef4444' },
        { value: '#f97316', label: 'Orange', hex: '#f97316' },
        { value: '#eab308', label: 'Yellow', hex: '#eab308' },
        { value: '#22c55e', label: 'Green', hex: '#22c55e' },
        { value: '#14b8a6', label: 'Teal', hex: '#14b8a6' },
        { value: '#3b82f6', label: 'Blue', hex: '#3b82f6' },
        { value: '#6366f1', label: 'Indigo', hex: '#6366f1' },
        { value: '#a855f7', label: 'Purple', hex: '#a855f7' },
        { value: '#ec4899', label: 'Pink', hex: '#ec4899' },
        { value: '#92400e', label: 'Brown', hex: '#92400e' },
        { value: '#6b7280', label: 'Gray', hex: '#6b7280' },
        { value: '#000000', label: 'Black', hex: '#000000' },
        { value: '#ffffff', label: 'White', hex: '#ffffff' },
      ];

      return (
        <div>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name={['classConfig', 'text_color']} label={<Text>Màu chữ</Text>} initialValue={'#ff922e'}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={COLOR_OPTIONS.map((c) => ({ value: c.value, label: (
                    <span className="flex items-center gap-2">
                      <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: c.hex, border: '1px solid #e5e7eb' }} />
                      {c.label}
                    </span>
                  ) }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.List name={['classConfig', 'items']} initialValue={[]}>
            {(fields, { add, remove }) => (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Text strong>Danh sách lớp</Text>
                  <Button type="dashed" onClick={() => add({ title: '', type: 1, link: '', image: '', description: '', bg_color: '', order: fields.length + 1 })}>Thêm mục</Button>
                </div>
                <div className="space-y-3">
                  {fields.map((field, idx) => {
                    const base = field.name;
                    return (
                      <div key={field.key} className="p-3 border rounded-md bg-white">
                        <div className="flex items-center gap-3 mb-2">
                          <Text strong>Mục #{idx + 1}</Text>
                          <Space className="ml-auto">
                            <Button danger type="text" onClick={() => remove(field.name)}>Xóa</Button>
                          </Space>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Form.Item name={[base, 'title']} label={<Text>Tiêu đề</Text>}>
                            <Input placeholder="Nhập tiêu đề..." />
                          </Form.Item>
                          <Form.Item name={[base, 'type']} label={<Text>Loại</Text>} initialValue={1}>
                            <Select
                              options={[
                                { value: 1, label: 'Nền bằng Style CSS' },
                                { value: 2, label: 'Nền bằng hình ảnh' },
                              ]}
                            />
                          </Form.Item>
                          <Form.Item name={[base, 'description']} label={<Text>Mô tả</Text>}>
                            <Input.TextArea placeholder="Nhập mô tả..." autoSize={{ minRows: 2 }} />
                          </Form.Item>
                          <Form.Item name={[base, 'link']} label={<Text>Đường dẫn</Text>}>
                            <Input placeholder="https://..." />
                          </Form.Item>

                          {/* Conditional: image when type=2 */}
                          <Form.Item noStyle shouldUpdate>
                            {() => {
                              const t = form.getFieldValue(['classConfig', 'items', idx, 'type']);
                              if (Number(t) === 2) {
                                const imageUrl = form.getFieldValue(['classConfig', 'items', idx, 'image']);
                                const handleClear = () => {
                                  const current = Array.isArray(form.getFieldValue(['classConfig', 'items'])) ? [...form.getFieldValue(['classConfig', 'items'])] : [];
                                  current[idx] = { ...(current[idx] || {}), image: '' };
                                  form.setFieldsValue({ classConfig: { ...(form.getFieldValue('classConfig') || {}), items: current } });
                                };
                                return (
                                  <div>
                                    <Form.Item name={[base, 'image']} label={<Text>Ảnh nền</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }] }>
                                      <Input
                                        placeholder="URL ảnh hoặc tải lên..."
                                        addonBefore={
                                          <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleUploadClassItemImageAt(file, idx)} className="hover:cursor-pointer">
                                            <UploadOutlined /> Upload
                                          </Upload>
                                        }
                                      />
                                    </Form.Item>
                                    {imageUrl && (
                                      <div className="relative inline-block" style={{ maxWidth: 220 }}>
                                        <img src={imageUrl} alt="thumb" style={{ width: '100%', height: 'auto', maxHeight: 160, objectFit: 'contain', borderRadius: 4, background: '#f2f3f5', border: '1px solid #f0f0f0' }} />
                                        <div className="absolute inset-0 flex items-center justify-center gap-2" style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 4 }}>
                                          <Button size="small" type="text" shape="circle" icon={<DownloadOutlined />} style={{ color: '#fff' }} onClick={() => {
                                            const a = document.createElement('a');
                                            a.href = imageUrl;
                                            a.download = '';
                                            a.target = '_blank';
                                            document.body.appendChild(a);
                                            a.click();
                                            a.remove();
                                          }} />
                                          <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleUploadClassItemImageAt(file, idx)}>
                                            <Button size="small" type="text" shape="circle" icon={<UploadOutlined />} style={{ color: '#fff' }} />
                                          </Upload>
                                          <Button size="small" type="text" shape="circle" icon={<DeleteOutlined />} onClick={handleClear} style={{ color: '#fff' }} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            }}
                          </Form.Item>

                          {/* Conditional: bg-color when type=1 */}
                          <Form.Item noStyle shouldUpdate>
                            {() => {
                              const t = form.getFieldValue(['classConfig', 'items', idx, 'type']);
                              if (Number(t) === 1) {
                                return (
                                  <Form.Item name={[base, 'bg_color']} label={<Text>Màu nền</Text>}>
                                    <Select
                                      showSearch
                                      optionFilterProp="label"
                                      options={COLOR_OPTIONS.map((c) => ({ value: c.value, label: (
                                        <span className="flex items-center gap-2">
                                          <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: c.hex, border: '1px solid #e5e7eb' }} />
                                          {c.label}
                                        </span>
                                      ) }))}
                                    />
                                  </Form.Item>
                                );
                              }
                              return null;
                            }}
                          </Form.Item>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Form.List>
        </div>
      );
    }
    // Custom value editor for footer-config
    if (isFooterConfig) {
      return (
        <div>
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Form.Item name={['footerConfig', 'gioi_thieu']} label={<Text>Giới thiệu</Text>}>
                <Input.TextArea placeholder="Nhập nội dung giới thiệu..." autoSize={{ minRows: 4 }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={['footerConfig', 'ho_tro']} label={<Text>Hỗ trợ</Text>} initialValue={[]}>
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Chọn bài viết hỗ trợ"
                  options={footerPostOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={['footerConfig', 'huong_dan']} label={<Text>Hướng dẫn</Text>} initialValue={[]}>
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Chọn bài viết hướng dẫn"
                  options={footerPostOptions}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
      );
    }
    // Custom value editor for web-params
    if (isWebParams) {
      // Upload handlers for specific web-params fields
      const handleUploadToField = async (file, namePath, folder = 'toancohien_web_params') => {
        const isValid = checkValidUpload(file);
        if (!isValid) return false;
        const url = await uploadImageCDN(file, () => {}, folder);
        if (url) {
          const safeUrl = encodeURI(url);
          // namePath is like ['webParams','favicon'] or ['webParams','detail_top_banner','image']
          // Update nested value via setFieldsValue so it is part of form values
          const curr = form.getFieldValue('webParams') || {};
          form.setFieldsValue({ webParams: setDeep(curr, namePath.slice(1), safeUrl) });
          notification.success({ message: 'Tải lên thành công!' });
        }
        return false;
      };

      // small util to immutably set deep value
      const setDeep = (obj, pathArr, value) => {
        if (!Array.isArray(pathArr) || pathArr.length === 0) return obj;
        const [head, ...rest] = pathArr;
        if (rest.length === 0) return { ...obj, [head]: value };
        return { ...obj, [head]: setDeep(obj?.[head] || {}, rest, value) };
      };

      const colorOptions = [
        { value: '#f26222', label: 'Mặc định', hex: '#f26222' },
        { value: '#ef4444', label: 'Red', hex: '#ef4444' },
        { value: '#f97316', label: 'Orange', hex: '#f97316' },
        { value: '#eab308', label: 'Yellow', hex: '#eab308' },
        { value: '#22c55e', label: 'Green', hex: '#22c55e' },
        { value: '#14b8a6', label: 'Teal', hex: '#14b8a6' },
        { value: '#3b82f6', label: 'Blue', hex: '#3b82f6' },
        { value: '#6366f1', label: 'Indigo', hex: '#6366f1' },
        { value: '#a855f7', label: 'Purple', hex: '#a855f7' },
        { value: '#ec4899', label: 'Pink', hex: '#ec4899' },
        { value: '#92400e', label: 'Brown', hex: '#92400e' },
        { value: '#6b7280', label: 'Gray', hex: '#6b7280' },
        { value: '#000000', label: 'Black', hex: '#000000' },
        { value: '#ffffff', label: 'White', hex: '#ffffff' },
      ];

      const RenderThumbUpload = ({ label, namePath, placeholder = 'Chọn / Upload ảnh' }) => (
        <Form.Item label={<Text>{label}</Text>}>
          {/* Hidden field to register this value so it appears in onFinish data */}
          <Form.Item name={namePath} hidden>
            <Input />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const url = form.getFieldValue(namePath);
              const handleClear = () => {
                // Clear via setFieldsValue to ensure inclusion in form values
                const curr = form.getFieldValue('webParams') || {};
                form.setFieldsValue({ webParams: setDeep(curr, namePath.slice(1), '') });
              };
              if (url) {
                return (
                  <div className="relative inline-block" style={{ maxWidth: 220 }}>
                    <img src={url} alt="thumb" style={{ width: '100%', height: 'auto', maxHeight: 160, objectFit: 'contain', borderRadius: 4, background: '#f2f3f5', border: '1px solid #f0f0f0' }} />
                    <div className="absolute inset-0 flex items-center justify-center gap-2" style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 4 }}>
                      <Button size="small" type="text" shape="circle" icon={<DownloadOutlined />} style={{ color: '#fff' }} onClick={() => {
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = '';
                        a.target = '_blank';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                      }} />
                      <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleUploadToField(file, namePath)}>
                        <Button size="small" type="text" shape="circle" icon={<UploadOutlined />} style={{ color: '#fff' }} />
                      </Upload>
                      <Button size="small" type="text" shape="circle" icon={<DeleteOutlined />} onClick={handleClear} style={{ color: '#fff' }} />
                    </div>
                  </div>
                );
              }
              return (
                <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleUploadToField(file, namePath)}>
                  <Button type="dashed" icon={<UploadOutlined />}>{placeholder}</Button>
                </Upload>
              );
            }}
          </Form.Item>
        </Form.Item>
      );

      return (
        <div>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name={['webParams', 'base_color']} label={<Text>Màu chủ đạo</Text>} initialValue={'black'}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={colorOptions.map((c) => ({ value: c.value, label: (
                    <span className="flex items-center gap-2">
                      <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: c.hex, border: '1px solid #e5e7eb' }} />
                      {c.label}
                    </span>
                  ) }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={['webParams', 'web_title']} label={<Text>Tiêu đề website</Text>}>
                <Input placeholder="Nhập tiêu đề website..." />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name={['webParams', 'phone']} label={<Text>Số điện thoại</Text>}>
                <Input placeholder="Nhập số điện thoại..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={['webParams', 'facebook_link']} label={<Text>Facebook</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }] }>
                <Input placeholder="https://facebook.com/..." />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {RenderThumbUpload({ label: 'Favicon', namePath: ['webParams', 'favicon'] })}
            </Col>
            <Col xs={24} md={12}>
              {RenderThumbUpload({ label: 'Logo', namePath: ['webParams', 'logo'] })}
            </Col>
            <Col xs={24} md={12}>
              {RenderThumbUpload({ label: 'Ảnh chia sẻ', namePath: ['webParams', 'share_image'] })}
            </Col>
            <Col xs={24} md={12}>
              {RenderThumbUpload({ label: 'Ảnh đồng hồ', namePath: ['webParams', 'clock_icon'] })}
            </Col>
            <Col xs={24} md={12}>
              {RenderThumbUpload({ label: 'Icon động (GIF)', namePath: ['webParams', 'gif_icon'] })}
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={['webParams', 'home_video_url']} label={<Text>Link video trang chủ</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }] }>
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name={['webParams', 'home_message']} label={<Text>Thông điệp trang chủ</Text>}>
                <Input.TextArea placeholder="Nhập thông điệp..." autoSize={{ minRows: 2 }} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name={['webParams', 'home_description']} label={<Text>Mô tả trang chủ (HTML)</Text>} valuePropName="value" trigger="onEditorChange" getValueFromEvent={(content) => (typeof content === 'string' ? content : '')}>
                <BundledEditor
                  init={{
                    license_key: 'gpl',
                    height: 400,
                    menubar: false,
                    automatic_uploads: true,
                    file_picker_types: 'image',
                    plugins: ['advlist','autolink','lists','link','image','charmap','preview','anchor','searchreplace','visualblocks','code','fullscreen','insertdatetime','media','table','help','wordcount'],
                    toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | image | removeformat | code | help',
                    extended_valid_elements: 'iframe[src|frameborder|style|scrolling|class|width|height|name|align|title|sandbox],a[name|href|target|rel],script[src|type|defer|async],div[*]',
                    file_picker_callback: (cb, value, meta) => {
                      const input = document.createElement('input');
                      input.setAttribute('type', 'file');
                      input.setAttribute('accept', 'image/*');
                      input.onchange = async () => {
                        const file = input.files[0];
                        if (!checkValidUpload(file)) return;
                        const url = await uploadImageCDN(file, () => {}, 'toancohien_web_params');
                        if (url) {
                          const safeUrl = encodeURI(url);
                          cb(safeUrl, { title: file.name });
                        }
                      };
                      input.click();
                    },
                    images_upload_handler: async (blobInfo, progress) => {
                      const blob = blobInfo.blob();
                      if (!checkValidUpload(blob)) return '';
                      const url = await uploadImageCDN(blob, () => {}, 'toancohien_web_params');
                      return encodeURI(url || '');
                    },
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item name={['webParams', 'articles']} label={<Text>Học toán cùng cô Hiền</Text>} initialValue={0}>
                <Radio.Group>
                  <Radio value={0}>Tắt</Radio>
                  <Radio value={1}>Bật</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              {RenderThumbUpload({ label: 'Ảnh nền thanh điều hướng', namePath: ['webParams', 'nav_bg_image'] })}
            </Col>
            <Col xs={24} md={12}>
              {RenderThumbUpload({ label: 'Ảnh nền chân trang', namePath: ['webParams', 'footer_bg_image'] })}
            </Col>
            <Col xs={24} md={12}>
              {RenderThumbUpload({ label: 'Banner trên trang chi tiết', namePath: ['webParams', 'detail_top_banner'] })}
            </Col>
            <Col xs={24} md={12}>
              {RenderThumbUpload({ label: 'Banner học thử', namePath: ['webParams', 'trial_banner'] })}
            </Col>
            <Col xs={24} md={12}>
              {RenderThumbUpload({ label: 'Ảnh Banner giữa', namePath: ['webParams', 'center_home_banner'] })}
            </Col>

          </Row>
          <div className="mt-4 p-3 border rounded-md">
            <div className="flex justify-between items-center mb-2">
              <Text strong>Banner dưới cùng của trang chi tiết</Text>
              <Form.List name={['webParams', 'detail_bottom_banner']} initialValue={[]}>
                {(fields, { add, remove }) => (
                  <div className="w-full">
                    <div className="flex justify-end mb-2">
                      <Button type="dashed" onClick={() => add({ image: '', url: '' })}>Thêm ảnh</Button>
                    </div>
                    <div className="space-y-3">
                      {fields.map((field, idx) => (
                        <div key={field.key} className="p-3 border rounded-md bg-white">
                          <div className="flex items-center gap-3 mb-2">
                            <Text strong>Banner #{idx + 1}</Text>
                            <Space className="ml-auto">
                              <Button danger type="text" onClick={() => remove(field.name)}>Xóa</Button>
                            </Space>
                          </div>
                          <Row gutter={[16, 0]}>
                            <Col xs={24} md={12}>
                              {RenderThumbUpload({ label: 'Ảnh', namePath: ['webParams', 'detail_bottom_banner', idx, 'image'] })}
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item name={[field.name, 'url']} label={<Text>Đường dẫn</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }] }>
                                <Input placeholder="https://..." />
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Form.List>
            </div>
          </div>
        </div>
      );
    }
    // Custom value editor for news-box
    if (isNewsBox) {
      const handleUploadNewsIconAt = async (file, index) => {
        const isValid = checkValidUpload(file);
        if (!isValid) return false;
        const url = await uploadImageCDN(file, () => {}, 'toancohien_config_news_icon');
        if (url) {
          const safeUrl = encodeURI(url);
          const current = Array.isArray(form.getFieldValue('newsItems')) ? [...form.getFieldValue('newsItems')] : [];
          current[index] = { ...(current[index] || {}), icon: safeUrl };
          form.setFieldsValue({ newsItems: current });
          notification.success({ message: 'Tải lên thành công!' });
        }
        return false;
      };
      return (
        <Form.List name="newsItems" initialValue={[]}>
          {(fields, { add, remove }) => (
            <div>
              <div className="flex justify-between items-center mb-2">
                <Text>Danh sách mục News Box</Text>
                <Button type="dashed" onClick={() => add({ title: '', icon: '', color: 'black', order: fields.length + 1, post_id: undefined })}>Thêm mục</Button>
              </div>
              <div className="space-y-3">
                {fields.map((field, idx) => {
                  const base = field.name;
                  return (
                    <div key={field.key} className="p-3 border rounded-md bg-white">
                      <div className="flex items-center gap-3 mb-2">
                        <Text strong>Mục #{idx + 1}</Text>
                        <Space className="ml-auto">
                          <Button danger type="text" onClick={() => remove(field.name)}>Xóa</Button>
                        </Space>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Form.Item name={[base, 'title']} label={<Text>Danh mục tin tức</Text>}>
                          <Select
                            showSearch
                            allowClear
                            placeholder="Chọn danh mục (con của danh mục #23)"
                            optionFilterProp="label"
                            options={newsCategoryOptions}
                            onChange={(val) => {
                              const current = Array.isArray(form.getFieldValue('newsItems')) ? [...form.getFieldValue('newsItems')] : [];
                              current[idx] = { ...(current[idx] || {}), title: val, post_id: undefined };
                              form.setFieldsValue({ newsItems: current });
                            }}
                          />
                        </Form.Item>
                        <Form.Item name={[base, 'order']} label={<Text>Thứ tự</Text>} initialValue={idx + 1}>
                          <Input type="number" min={1} placeholder="VD: 1" />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {() => {
                            const catId = Number(form.getFieldValue(['newsItems', idx, 'title']) || 0);
                            let list = (newsPostsByCat && newsPostsByCat[catId]) ? newsPostsByCat[catId] : [];
                            if (!Array.isArray(list) || list.length === 0) {
                              // Fallback: filter from aggregated posts by category 23
                              const base = Array.isArray(postsCat23?.data) ? postsCat23.data : [];
                              const inCat = (p) => {
                                const single = Number(p?.category_id ?? p?.categoryId ?? (p?.category?.id ?? 0));
                                if (single === catId) return true;
                                const a1 = Array.isArray(p?.category_ids) ? p.category_ids : [];
                                if (a1.some((x) => Number(x) === catId)) return true;
                                const a2 = Array.isArray(p?.categories) ? p.categories : [];
                                if (a2.some((x) => Number(x) === catId || Number(x?.id) === catId)) return true;
                                return false;
                              };
                              list = base.filter(inCat);
                            }
                            const opts = (Array.isArray(list) ? list : []).map((p) => ({ value: p.id, label: p.title }));
                            return (
                              <Form.Item name={[base, 'post_id']} label={<Text>Bài viết</Text>}>
                                <Select
                                  showSearch
                                  allowClear
                                  placeholder={catId > 0 ? 'Chọn bài viết' : 'Chọn danh mục trước'}
                                  optionFilterProp="label"
                                  options={opts}
                                  disabled={catId <= 0}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                        <Form.Item name={[base, 'color']} label={<Text>Màu sắc</Text>}>
                          {(() => {
                            const BASIC_COLORS = [
                              { value: 'red', label: 'Red', hex: '#ef4444' },
                              { value: 'orange', label: 'Orange', hex: '#f97316' },
                              { value: 'yellow', label: 'Yellow', hex: '#eab308' },
                              { value: 'green', label: 'Green', hex: '#22c55e' },
                              { value: 'teal', label: 'Teal', hex: '#14b8a6' },
                              { value: 'blue', label: 'Blue', hex: '#3b82f6' },
                              { value: 'indigo', label: 'Indigo', hex: '#6366f1' },
                              { value: 'purple', label: 'Purple', hex: '#a855f7' },
                              { value: 'pink', label: 'Pink', hex: '#ec4899' },
                              { value: 'brown', label: 'Brown', hex: '#92400e' },
                              { value: 'gray', label: 'Gray', hex: '#6b7280' },
                              { value: 'black', label: 'Black', hex: '#000000' },
                              { value: 'white', label: 'White', hex: '#ffffff' },
                            ];
                            return (
                              <Select
                                showSearch
                                optionFilterProp="label"
                                allowClear
                                placeholder="Chọn màu"
                                options={BASIC_COLORS.map(c => ({ value: c.value, label: (
                                  <span className="flex items-center gap-2">
                                    <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: c.hex, border: '1px solid #e5e7eb' }} />
                                    {c.label}
                                  </span>
                                ) }))}
                              />
                            );
                          })()}
                        </Form.Item>
                        <Form.Item label={<Text>Ảnh Icon</Text>}>
                          <Form.Item noStyle shouldUpdate>
                            {() => {
                              const imageUrl = form.getFieldValue(['newsItems', idx, 'icon']);
                              const handleClear = () => {
                                const current = Array.isArray(form.getFieldValue('newsItems')) ? [...form.getFieldValue('newsItems')] : [];
                                current[idx] = { ...(current[idx] || {}), icon: '' };
                                form.setFieldsValue({ newsItems: current });
                              };
                              if (imageUrl) {
                                return (
                                  <div className="relative inline-block" style={{ maxWidth: 200 }}>
                                    <img
                                      src={imageUrl}
                                      alt="icon-thumb"
                                      style={{ width: '100%', height: 'auto', maxHeight: 160, objectFit: 'contain', borderRadius: 4, background: '#f2f3f5', border: '1px solid #f0f0f0' }}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center gap-2" style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 4 }}>
                                      <Button size="small" type="text" shape="circle" icon={<DownloadOutlined />} style={{ color: '#fff' }} onClick={() => {
                                        const a = document.createElement('a');
                                        a.href = imageUrl;
                                        a.download = '';
                                        a.target = '_blank';
                                        document.body.appendChild(a);
                                        a.click();
                                        a.remove();
                                      }} />
                                      <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleUploadNewsIconAt(file, idx)}>
                                        <Button size="small" type="text" shape="circle" icon={<UploadOutlined />} style={{ color: '#fff' }} />
                                      </Upload>
                                      <Button size="small" type="text" shape="circle" icon={<DeleteOutlined />} onClick={handleClear} style={{ color: '#fff' }} />
                                    </div>
                                  </div>
                                );
                              }
                              return (
                                <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleUploadNewsIconAt(file, idx)}>
                                  <Button type="dashed" icon={<UploadOutlined />}>Chọn / Upload ảnh</Button>
                                </Upload>
                              );
                            }}
                          </Form.Item>
                        </Form.Item>
                      </div>
                      
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Form.List>
      );
    }
    if (Number(type || 1) === 3) {
      return (
        <Form.Item name="value" label={<Text>Giá trị (URL ảnh)</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }] }>
          <Input
            placeholder="Nhập URL hoặc tải lên ảnh..."
            addonBefore={
              <Upload accept="image/*" showUploadList={false} beforeUpload={handleUploadImage} className="hover:cursor-pointer">
                <UploadOutlined /> Upload
              </Upload>
            }
          />
        </Form.Item>
      );
    }
    if (Number(type || 1) === 5) {
      return (
        <Form.List name="slides" initialValue={[]}>
          {(fields, { add, remove }, { move }) => {
            const handleUploadAt = async (file, namePath) => {
              const isValid = checkValidUpload(file);
              if (!isValid) return false;
              const url = await uploadImageCDN(file, () => {}, 'toancohien_config_slide');
              if (url) {
                const safeUrl = encodeURI(url);
                // antd v4 friendly update: patch slides array then setFieldsValue
                const slides = Array.isArray(form.getFieldValue('slides')) ? [...form.getFieldValue('slides')] : [];
                const idx = Array.isArray(namePath) ? Number(namePath[0]) : -1;
                if (idx >= 0) {
                  slides[idx] = { ...(slides[idx] || {}), image: safeUrl };
                  form.setFieldsValue({ slides });
                }
                notification.success({ message: 'Tải lên thành công!' });
              }
              return false;
            };
            return (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Text>Danh sách Slide</Text>
                  <Button type="dashed" onClick={() => add({ title: '', image: '', link: '', order: fields.length + 1 })}>Thêm slide</Button>
                </div>
                <div className="space-y-3">
                  {fields.map((field, idx) => {
                    const base = field.name;
                    return (
                      <div key={field.key} className="p-3 border rounded-md bg-white">
                        <div className="flex items-center gap-3 mb-2">
                          <Text strong>Slide #{idx + 1}</Text>
                          <Space className="ml-auto">
                            <Button danger type="text" onClick={() => remove(field.name)}>Xóa</Button>
                          </Space>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Form.Item name={[base, 'title']} label={<Text>Tiêu đề</Text>}>
                            <Input placeholder="Nhập tiêu đề..." />
                          </Form.Item>
                          <Form.Item name={[base, 'order']} label={<Text>Thứ tự</Text>} initialValue={idx + 1}>
                            <Input type="number" min={1} placeholder="VD: 1" />
                          </Form.Item>
                          <Form.Item name={[base, 'link']} label={<Text>Đường dẫn</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }] }>
                            <Input placeholder="https://..." />
                          </Form.Item>
                          <Form.Item
                            name={[base, 'image']}
                            label={<Text>Hình ảnh <span className="text-red-700">*</span></Text>}
                            rules={[{ required: true, message: 'Vui lòng chọn ảnh' }]}
                          >
                            <Input
                              placeholder="URL ảnh hoặc tải lên..."
                              addonBefore={
                                <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleUploadAt(file, [base, 'image'])} className="hover:cursor-pointer">
                                  <UploadOutlined /> Upload
                                </Upload>
                              }
                            />
                          </Form.Item>
                        </div>
                        <Form.Item noStyle shouldUpdate>
                          {() => {
                            const imageUrl = form.getFieldValue(['slides', idx, 'image']);
                            return imageUrl ? (
                              <div className="w-full md:w-1/2 relative">
                                <img
                                  src={imageUrl}
                                  alt="thumb"
                                  style={{ width: '100%', height: 'auto', maxHeight: 240, objectFit: 'contain', borderRadius: 4, background: '#f2f3f5', border: '1px solid #f0f0f0' }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center" style={{ pointerEvents: 'none' }}>
                                  <Button size="small" type="text" shape="circle" icon={<DownloadOutlined />} style={{ color: '#1677ff', background: 'rgba(255,255,255,0.8)' }} onClick={() => {
                                    const a = document.createElement('a');
                                    a.href = imageUrl;
                                    a.download = '';
                                    a.target = '_blank';
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                  }} />
                                </div>
                              </div>
                            ) : null;
                          }}
                        </Form.Item>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }}
        </Form.List>
      );
    }
    if (Number(type || 1) === 6) {
      const handleUploadBanner = async (file) => {
        const isValid = checkValidUpload(file);
        if (!isValid) return false;
        const url = await uploadImageCDN(file, () => {}, 'toancohien_config_banner');
        if (url) {
          const safeUrl = encodeURI(url);
          const banner = form.getFieldValue('banner') || {};
          form.setFieldsValue({ banner: { ...banner, image: safeUrl } });
          notification.success({ message: 'Tải lên thành công!' });
        }
        return false;
      };
      return (
        <div>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item name={["banner", "link"]} label={<Text>Đường dẫn</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }]}>
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={["banner", "image"]}
                label={<Text>Hình ảnh <span className="text-red-700">*</span></Text>}
                rules={[{ required: true, message: 'Vui lòng chọn ảnh' }]}
              >
                <Input
                  placeholder="URL ảnh hoặc tải lên..."
                  addonBefore={
                    <Upload accept="image/*" showUploadList={false} beforeUpload={handleUploadBanner} className="hover:cursor-pointer">
                      <UploadOutlined /> Upload
                    </Upload>
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const imageUrl = form.getFieldValue(['banner', 'image']);
                  return imageUrl ? (
                    <div className="w-full relative">
                      <img
                        src={imageUrl}
                        alt="banner-thumb"
                        style={{ width: '100%', height: 'auto', maxHeight: 240, objectFit: 'contain', borderRadius: 4, background: '#f2f3f5', border: '1px solid #f0f0f0' }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center" style={{ pointerEvents: 'none' }}>
                        <Button size="small" type="text" shape="circle" icon={<DownloadOutlined />} style={{ color: '#1677ff', background: 'rgba(255,255,255,0.8)' }} onClick={() => {
                          const a = document.createElement('a');
                          a.href = imageUrl;
                          a.download = '';
                          a.target = '_blank';
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                        }} />
                      </div>
                    </div>
                  ) : null;
                }}
              </Form.Item>
            </Col>
          </Row>
        </div>
      );
    }
    if (Number(type || 1) === 7) {
      return (
        <Form.List name="album" initialValue={['']}>
          {(fields, { add, remove }) => {
            const handleUploadAlbumAt = async (file, index) => {
              const isValid = checkValidUpload(file);
              if (!isValid) return false;
              const url = await uploadImageCDN(file, () => {}, 'toancohien_config_album');
              if (url) {
                const safeUrl = encodeURI(url);
                const album = Array.isArray(form.getFieldValue('album')) ? [...form.getFieldValue('album')] : [];
                album[index] = safeUrl;
                form.setFieldsValue({ album });
                notification.success({ message: 'Tải lên thành công!' });
              }
              return false;
            };
            return (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Text>Danh sách ảnh</Text>
                  <Button type="dashed" onClick={() => add('')}>Thêm ảnh</Button>
                </div>
                {fields.map((field, idx) => (
                  <div key={field.key} className="p-3 border rounded-md bg-white">
                    <div className="flex items-center gap-3 mb-2">
                      <Text strong>Ảnh #{idx + 1}</Text>
                      <Space className="ml-auto">
                        <Button danger type="text" onClick={() => remove(field.name)}>Xóa</Button>
                      </Space>
                    </div>
                    <Form.Item name={field.name} label={<Text>Đường dẫn ảnh</Text>} rules={[{ type: 'url', message: 'Đường dẫn không hợp lệ!' }]}>
                      <Input
                        placeholder="URL ảnh hoặc tải lên..."
                        addonBefore={
                          <Upload accept="image/*" showUploadList={false} beforeUpload={(file) => handleUploadAlbumAt(file, idx)} className="hover:cursor-pointer">
                            <UploadOutlined /> Upload
                          </Upload>
                        }
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const url = form.getFieldValue(['album', idx]);
                        return url ? (
                          <div className="relative">
                            <img src={url} alt="thumb" style={{ width: '100%', height: 'auto', maxHeight: 200, objectFit: 'contain', borderRadius: 4, background: '#f2f3f5', border: '1px solid #f0f0f0' }} />
                            <div className="absolute inset-0 flex items-center justify-center" style={{ pointerEvents: 'none' }}>
                              <Button size="small" type="text" shape="circle" icon={<DownloadOutlined />} style={{ color: '#1677ff', background: 'rgba(255,255,255,0.8)' }} onClick={() => {
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = '';
                                a.target = '_blank';
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                              }} />
                            </div>
                          </div>
                        ) : null;
                      }}
                    </Form.Item>
                  </div>
                ))}
              </div>
            );
          }}
        </Form.List>
      );
    }
    if (Number(type || 1) === 4) {
      return (
        <Form.Item name="value" label={<Text>Giá trị (HTML)</Text>} valuePropName="value" trigger="onEditorChange" getValueFromEvent={(content) => (typeof content === 'string' ? content : '')}>
          <BundledEditor
            init={{
              license_key: 'gpl',
              height: 400,
              menubar: false,
              plugins: ['advlist','autolink','lists','link','image','charmap','preview','anchor','searchreplace','visualblocks','code','fullscreen','insertdatetime','media','table','help','wordcount'],
              toolbar: 'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | code | help',
            }}
          />
        </Form.Item>
      );
    }
    const placeholder = Number(type || 1) === 2 ? 'Nhập JSON hợp lệ...' : 'Nhập giá trị...';
    return (
      <Form.Item name="value" label={<Text>Giá trị</Text>}>
        <Input.TextArea placeholder={placeholder} autoSize={{ minRows: 3 }} />
      </Form.Item>
    );
  };

  return (
    <Form name="edit-config" form={form} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Sửa {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item name="key" label={<Text>Key <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: 'Vui lòng nhập key' }]}>
            <Input placeholder="Nhập key cấu hình (duy nhất)..." disabled={true} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="title" label={<Text>Tiêu đề <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: `Vui lòng nhập tiêu đề ${itemName}` }]}>
            <Input placeholder={`Nhập tiêu đề ${itemName}...`} disabled={true} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="type" label={<Text>Loại <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: 'Vui lòng chọn loại' }]} initialValue={1}>
            <Select showSearch optionFilterProp="label" options={TYPE_OPTIONS} disabled={true} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="status" label={<Text>Trạng thái <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]} initialValue={1}>
            <Select showSearch optionFilterProp="label" options={STATUS_OPTIONS} disabled={true} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item name="note" label={<Text>Ghi chú</Text>}>
            <Input.TextArea placeholder="Ghi chú..." autoSize={{ minRows: 2 }} disabled={true} />
          </Form.Item>
        </Col>

        <Col xs={24}>{renderValueField()}</Col>

        <Col xs={24}>
          <Row justify="end" style={{ marginTop: 10 }}>
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>
                  Hủy bỏ
                </Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>
                  Lưu lại
                </Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default EditConfig;
